import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Alert,
  IconButton,
} from '@mui/material';
import { Description, Close } from '@mui/icons-material';
import { WriteoffView } from '../../../../../services/WriteoffService/types';
import { WriteoffDocumentView } from '../../../../../services/WriteoffService/types/documentTypes';
import {
  DOCUMENT_VIEW_DIALOG_STYLES,
  UPLOAD_DIALOG_COLORS,
} from '../../../../common/styles/dialogStyles';
import { useDocumentActions } from './hooks';
import { DocumentHeader, DocumentInfo, DocumentActions } from './components';

export interface ViewDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  document: WriteoffDocumentView | null;
  writeoff: WriteoffView;
  onDocumentDeleted?: (documentId: string) => void;
}

export const ViewDocumentDialog: React.FC<ViewDocumentDialogProps> = ({
  open,
  onClose,
  document: documentInfo,
  writeoff,
  onDocumentDeleted,
}) => {
  const {
    isDownloading,
    isViewing,
    isDeleting,
    canDelete,
    handleDownload,
    handleView,
    handleDelete,
  } = useDocumentActions({ documentInfo, writeoff, onClose, onDocumentDeleted });

  if (!documentInfo) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: DOCUMENT_VIEW_DIALOG_STYLES.paper }}
    >
      <DialogTitle sx={DOCUMENT_VIEW_DIALOG_STYLES.title}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Description sx={{ color: UPLOAD_DIALOG_COLORS.primary, fontSize: 28 }} />
          Document Details
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={DOCUMENT_VIEW_DIALOG_STYLES.content}>
        <Stack spacing={3}>
          <DocumentHeader document={documentInfo} />
          <DocumentInfo document={documentInfo} />
          <DocumentActions
            canDelete={canDelete}
            isDownloading={isDownloading}
            isViewing={isViewing}
            isDeleting={isDeleting}
            onView={handleView}
            onDownload={handleDownload}
            onDelete={handleDelete}
          />

          {!canDelete && (
            <Alert severity="info" sx={DOCUMENT_VIEW_DIALOG_STYLES.infoAlert}>
              <Typography variant="body2">
                You can only delete documents that you have uploaded.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={DOCUMENT_VIEW_DIALOG_STYLES.actions}>
        <Button onClick={onClose} variant="outlined" sx={DOCUMENT_VIEW_DIALOG_STYLES.closeButton}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewDocumentDialog;
